import { useRef, useCallback, useEffect } from 'react'

/**
 * Hook for managing conversation scroll behavior
 * Handles auto-scroll, scroll position saving/restoring, and user scroll detection
 */
export function useConversationScroll() {
  const messagesEndRef = useRef(null)
  const conversationContainerRef = useRef(null)
  const thinkingBubbleRef = useRef(null)
  const savedScrollPositionRef = useRef(null)
  const userIsScrollingRef = useRef(false)
  const scrollTimeoutRef = useRef(null)
  const previousContentHashRef = useRef(null)

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  const scrollToThinkingBubble = useCallback(() => {
    thinkingBubbleRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  // Generate content hash for conversation to detect changes
  const generateContentHash = useCallback((ticket) => {
    if (!ticket?.conversation_thread) return 'empty'

    const messages = ticket.merged_conversation_thread || ticket.conversation_thread || []

    const contentData = {
      messages: messages.map(msg => ({
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp,
      })),
      status: ticket.status,
      role: ticket.role,
      modified_at: ticket.modified_at,
      childCount: ticket.child_tickets?.length || 0
    }

    return JSON.stringify(contentData)
  }, [])

  // Save scroll position with context
  const saveScrollPosition = useCallback(() => {
    if (conversationContainerRef.current) {
      const container = conversationContainerRef.current
      const scrollData = {
        scrollTop: container.scrollTop,
        scrollHeight: container.scrollHeight,
        clientHeight: container.clientHeight,
        isAtBottom: container.scrollTop + container.clientHeight >= container.scrollHeight - 10
      }
      savedScrollPositionRef.current = scrollData
    }
  }, [])

  // Restore scroll position
  const restoreScrollPosition = useCallback(() => {
    if (conversationContainerRef.current && savedScrollPositionRef.current) {
      const container = conversationContainerRef.current
      const saved = savedScrollPositionRef.current

      // Don't auto-scroll if user is actively scrolling
      if (userIsScrollingRef.current) {
        return
      }

      // If user was at bottom before, keep them at bottom
      if (saved.isAtBottom) {
        setTimeout(() => scrollToBottom(), 0)
      } else if (typeof saved.scrollTop === 'number' && saved.scrollTop > 0) {
        const maxScrollTop = container.scrollHeight - container.clientHeight
        if (saved.scrollTop <= maxScrollTop) {
          container.scrollTop = saved.scrollTop
        } else {
          setTimeout(() => scrollToBottom(), 0)
        }
      }
    }
  }, [scrollToBottom])

  // Reset scroll state
  const resetScrollState = useCallback(() => {
    previousContentHashRef.current = null
    savedScrollPositionRef.current = null
  }, [])

  // Check if user was at bottom
  const wasAtBottom = useCallback(() => {
    return savedScrollPositionRef.current?.isAtBottom ?? true
  }, [])

  // Clear saved position (used when auto-scrolling)
  const clearSavedPosition = useCallback(() => {
    savedScrollPositionRef.current = null
  }, [])

  // Detect user scrolling
  useEffect(() => {
    const container = conversationContainerRef.current
    if (!container) return

    const handleScroll = () => {
      userIsScrollingRef.current = true

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }

      // Reset the flag after user stops scrolling for 2 seconds
      scrollTimeoutRef.current = setTimeout(() => {
        userIsScrollingRef.current = false
      }, 2000)
    }

    container.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      container.removeEventListener('scroll', handleScroll)
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
    }
  }, [])

  return {
    // Refs to attach to DOM elements
    messagesEndRef,
    conversationContainerRef,
    thinkingBubbleRef,

    // Scroll functions
    scrollToBottom,
    scrollToThinkingBubble,

    // Scroll position management
    saveScrollPosition,
    restoreScrollPosition,
    resetScrollState,
    clearSavedPosition,
    wasAtBottom,

    // Content hash for change detection
    generateContentHash,
    previousContentHashRef,

    // User scroll state
    userIsScrollingRef
  }
}

export default useConversationScroll
